<template>
  <!-- todo: 'size' is used for temporary purposes. We will remove this in our final version -->
  <dn-collection
    :cid="`drEdition__${id}`"
    class="dn-edition-collection"
    :provider="options?.provider || 'drEdition'"
    :options="{ ...options, id }"
    :fnc="getAwaitList"
  >
    <template #default="data">
      <slot name="edition-top-prefix" />
      <slot name="leftColumn" />
      <slot name="rightColumn" />

      <dn-collection-divider
        v-if="data.collection"
        :collection="data.collection"
        :position="data.metadata?.fullWidthAfterRows"
        :lazy-load-position="data.metadata?.lazyLoadStartRow"
      >
        <template #divider_0="dividedData">
          <section
            class="dn-edition-collection-top"
            data-zone="page-top"
          >
            <slot name="edition-headline" />
            <slot
              v-if="dividedData.section"
              name="edition-top-part-prefix"
            />
            <dn-component
              v-for="(item, itemIdx) in dividedData.section"
              :key="itemIdx"
              v-bind="item"
            />
            <slot name="edition-top-part-suffix" />
          </section>
        </template>
        <template #divider_1="dividedData">
          <section
            class="dn-edition-collection-bottom"
            data-zone="page-bottom"
          >
            <slot
              v-if="dividedData.section"
              name="edition-bottom-part-prefix"
            />
            <dn-component
              v-for="(item, itemIdx) in dividedData.section"
              :key="itemIdx"
              v-bind="item"
            />
            <slot
              v-if="dividedData.section"
              name="edition-bottom-part-suffix"
            />
          </section>
        </template>
      </dn-collection-divider>
    </template>
  </dn-collection>
</template>
<script lang="ts" setup>
const props = defineProps({
  id: {
    type: String,
    required: true
  },
  options: {
    type: Object,
    required: false,
    default: () => {}
  }
});

const globalAwaitForList = ref([]); // TODO: provide logic for add off await for lists
const skipIds = ref([]);
const globalStatusList: Ref<{ key: { load: boolean } } | {}> = ref({});

provide('teaser_provider', 'articleTeaser');
provide('global_await_for_list', globalAwaitForList);
provide('global_skip_ids', skipIds);
provide('global_status_list', globalStatusList);
provide('prefetch', false);
let cleanup = [];

const getAwaitList = (val) => {
  const { items: editionItems } = useEditorsToplistCutoff().splitAtCutoff(val);
  const { items, globalAwaitForListNew } = findCppPlaceholders(editionItems);
  globalAwaitForList.value = globalAwaitForListNew;
  globalStatusList.value = {
    [props.id]: {
      load: true
    }
  };
  for (const key in globalAwaitForListNew) {
    globalStatusList.value = {
      ...globalStatusList.value,
      [key]: {
        load: false
      }
    };
  }
  for (const key in globalAwaitForList.value) {
    const listenerCleanup = useBusListener({
      [`DnCollection__collection__${key}`]: (_) => {
        globalStatusList.value[key].load = true;
        listenerCleanup();
      }
    });
    cleanup.push(listenerCleanup);
  }
  skipIds.value = [...items];
};

let collectionPayloadListener;
if (import.meta.server) {
  collectionPayloadListener = useBusListener({
    [`DnCollection__collection__drEdition__${props.id}`]: (data) => {
      if (data.updatedAt) {
        const event = useRequestEvent();
        if (event) {
          event.context.updatedAt = data.updatedAt;
        }
      }
    }
  });
}
if (collectionPayloadListener) {
  cleanup.push(collectionPayloadListener);
}

onBeforeUnmount(() => {
  if (cleanup && cleanup.length > 0) {
    cleanup.forEach(fn => fn && fn());
    cleanup = [];
  }
  globalAwaitForList.value = [];
  skipIds.value = [];
  globalStatusList.value = {};
});

function findCppPlaceholders (obj) {
  const items = [];
  const placeholders = [];

  function recursiveSearch (item) {
    if (Array.isArray(item)) {
      item.forEach(subItem => recursiveSearch(subItem));
    } else if (typeof item === 'object' && item !== null) {
      const { type, fields } = item;
      if (type === 'cpp-placeholder') {
        const typeKey = fields?.type?.split('-').slice(0, 2).join('-') || '';
        if (['cpp-collections', 'cpp-streams', 'cpp-categories', 'cpp-mostread', 'cpp-sections', 'cpp-latestnews', 'cpp-dataplatform', 'cpp-editortoplist'].includes(typeKey)) {
          const middleKey = fields.streamId || fields.contentsource;
          placeholders.push(middleKey ? `${fields.placeholder}__${middleKey}__${fields.id}` : `${fields.placeholder}__${fields.id}`);
        }
      } else if (item.type === 'news' || item.type === 'featureArticle' || item.type === 'video') {
        items.push(item.id || item);
      }
      for (const key in item) {
        if (Object.prototype.hasOwnProperty.call(item, key)) {
          recursiveSearch(item[key]);
        }
      }
    }
  }

  recursiveSearch(obj);

  const globalAwaitForListNew = placeholders.reduce((acc, item, index) => {
    acc[item] = placeholders.slice(0, index);
    return acc;
  }, {});
  return { globalAwaitForListNew, items };
}
</script>

<style lang="scss">
.dn-edition-collection {
  --_dn-edition-collection_grid-template: var(--dn-edition-collection_grid-template, 1fr);
  --_dn-edition-collection_gap: var(--dn-edition-collection_gap, 12px);
  --_dn-edition-collection-top_grid-area: var(--dn-edition-collection-top_grid-area, auto);
  --_dn-edition-collection-bottom_grid-area: var(--dn-edition-collection-bottom_grid-area, auto);
  --_dn-edition-collection-left_grid-area: var(--dn-edition-collection-left_grid-area, auto);
  --_dn-edition-collection-right_grid-area: var(--dn-edition-collection-right_grid-area, auto);
  --_dn-edition-collection--ad_grid-area: var(--dn-edition-collection-ad_grid-area, 1 / 3 / 1 / 4);

  &.dn-frontpage-collection {
    grid-template: var(--_dn-edition-collection_grid-template);

    @media only screen and (width >= 1328px) {
      inline-size: min(var(--grid-width), 100vw - var(--site-padding) * 2);
      margin-left: calc(50% - var(--grid-width) / 2);
    }
  }

  &-top,
  &-bottom {
    display: grid;
    grid-area: var(--_dn-edition-collection-top_grid-area);
    gap: var(--_dn-edition-collection_gap);
  }

  &-top {
    --dn-collection-content-width: var(--front-top-content-width);

    @media only screen and (width >= 1328px) {
      grid-template: auto / var(--front-top-content-width);
    }
  }

  &-bottom {
    --dn-collection-content-width: var(--feed-width);

    @media only screen and (width >= 1328px) {
      grid-template: auto / var(--feed-width);
      grid-area: var(--_dn-edition-collection-bottom_grid-area);
    }
  }

  .left-column {
    @media only screen and (width >= 1328px) {
      grid-area: var(--_dn-edition-collection-left_grid-area);
    }
  }

  .right-column {
    @media only screen and (width >= 1328px) {
      grid-area: var(--_dn-edition-collection-right_grid-area);
    }
  }

  .left-column,
  .right-column {
    display: none;

    @media only screen and (width >= 1328px) {
      display: block;
      position: relative;
      width: 300px;
      z-index: 11;
    }

    > :not(:nth-child(1 of .view-ads)) {
      margin-top: var(--row-gap);
    }
  }
}
</style>
